/**
 * SIG-005 — how big one prepared mark is allowed to be, in data-URL characters.
 *
 * The pair has to survive the server's `maxSignatureBytes * 2` on the sheet the
 * two marks are combined onto. One number here keeps the importer and the sheet
 * agreeing on the same ceiling instead of each carrying its own.
 */
import { inkOnly } from '../../lib/signatureImage.js';

export const MAX_ASSET_CHARS = 400_000;

/**
 * The boxes an imported mark is fitted into, largest first.
 *
 * A signature or a stamp arrives as a photograph far more often than as a clean
 * scan, and a photograph re-encoded losslessly is enormous: the paper grain that
 * a JPEG throws away is grain a PNG has to spell out pixel by pixel. A 580 KB
 * phone photo comes back out of a 1000x500 canvas as ~870 KB of base64 — over
 * any ceiling worth having, while the file the sender picked was well under the
 * 5 MB they were promised. So the size is negotiated rather than demanded: the
 * first box whose encoding fits is the one used, and full size is still the
 * first thing tried, so a small clean scan is not shrunk for nothing.
 */
export const ASSET_BOXES: Array<[width: number, height: number]> = [
  [1000, 500],
  [720, 360],
  [520, 260],
  [380, 190],
  [260, 130],
];

/**
 * The first encoding that fits the budget, or null if even the smallest box
 * does not. `encode` is handed in rather than done here so the choice can be
 * exercised without a canvas.
 */
export function fitWithinBudget(
  encode: (box: [width: number, height: number]) => string,
  budget: number = MAX_ASSET_CHARS,
): string | null {
  for (const box of ASSET_BOXES) {
    const data = encode(box);
    if (data.length < budget) return data;
  }
  return null;
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image(); image.onload = () => resolve(image); image.onerror = reject; image.src = src;
  });
}

/** The stamp is printed first; handwriting goes over it on the same 2:1 sheet. */
export async function combineSigningAssets(signature: string, stamp: string | null): Promise<string> {
  if (!stamp) return signature;
  const images = await Promise.all([loadImage(stamp), loadImage(signature)]);
  const canvas = document.createElement('canvas'); canvas.width = 1000; canvas.height = 500;
  const context = canvas.getContext('2d'); if (!context) throw new Error('canvas unavailable');
  images.forEach((image) => {
    const ratio = Math.min(canvas.width / image.width, canvas.height / image.height);
    const width = image.width * ratio, height = image.height * ratio;
    context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  });
  return canvas.toDataURL('image/png');
}

/**
 * 1.50 — the one image the boxes on the document show and the page sends.
 *
 * What is drawn on the pad and what was picked are kept apart until here. Both
 * go on one sheet, the handwriting above the stamp; otherwise it is whichever
 * there is, as it is. The preview and the post both ask this, so the boxes show
 * exactly what is signed with.
 */
export async function signingImage(drawn: string | null, uploaded: string | null): Promise<string | null> {
  const first = drawn ?? uploaded;
  return first ? combineSigningAssets(first, drawn ? uploaded : null) : null;
}

/**
 * 1.50 — a picture the signer picked, made ready to sign with: PNG or JPEG, at
 * most 5 MB, shrunk through `fitWithinBudget` until its encoding fits.
 *
 * Null is a picture that stays huge at the smallest box, which a mark on paper
 * never is — so it is said as its own thing rather than blamed on the file they
 * chose. A file the page does not take, or cannot read, rejects.
 */
export async function importSigningImage(file: File): Promise<string | null> {
  if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 5 * 1024 * 1024) throw new Error('invalid image');
  const src = URL.createObjectURL(file);
  let image: HTMLImageElement;
  try { image = await loadImage(src); } finally { URL.revokeObjectURL(src); }
  // Apply the same paper/grain removal as the sender stamp importer. Keep the
  // reading canvas bounded before pixel analysis, then crop excess photo area.
  const ratio = Math.min(1, ASSET_BOXES[0]![0] / image.width, ASSET_BOXES[0]![1] / image.height);
  const reading = document.createElement('canvas');
  reading.width = Math.max(1, Math.round(image.width * ratio));
  reading.height = Math.max(1, Math.round(image.height * ratio));
  const read = reading.getContext('2d', { willReadFrequently: true });
  if (!read) throw new Error('canvas unavailable');
  read.drawImage(image, 0, 0, reading.width, reading.height);
  const { pixels, bounds } = inkOnly(read.getImageData(0, 0, reading.width, reading.height).data, reading.width, reading.height, 'original');
  if (!bounds) throw new Error('image contains no ink');
  const margin = Math.round(Math.max(bounds.width, bounds.height) * .03) + 2;
  const left = Math.max(0, bounds.x - margin), top = Math.max(0, bounds.y - margin);
  const cleaned = document.createElement('canvas');
  cleaned.width = Math.min(reading.width, bounds.x + bounds.width + margin) - left;
  cleaned.height = Math.min(reading.height, bounds.y + bounds.height + margin) - top;
  const clean = cleaned.getContext('2d'); if (!clean) throw new Error('canvas unavailable');
  clean.putImageData(new ImageData(pixels, reading.width, reading.height), -left, -top);
  return fitWithinBudget(([maxWidth, maxHeight]) => {
    const ratio = Math.min(1, maxWidth / cleaned.width, maxHeight / cleaned.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(cleaned.width * ratio)); canvas.height = Math.max(1, Math.round(cleaned.height * ratio));
    const context = canvas.getContext('2d'); if (!context) throw new Error('canvas unavailable');
    context.drawImage(cleaned, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  });
}
